import { type IErrorApiResponse, type ISuccessApiResponse } from '@wyz/types';
import {
  OverridableQuery,
  UseMutationOptions,
  UseQueryOptions,
  emptyQueryError,
  restrictApi,
  useMutation,
  useQuery,
  useQueryClient,
} from '@wyz/utils';
import { useApi } from '~/common/context';
import { PurchaseQueryKeys } from '~/purchase/api';
import {
  IPayloadAddWcwCartItems,
  IWcwCart,
  IWcwCartValidationErrorResponse,
  PurchaseErrorCodeKeys,
} from '~/purchase/types';

const EMPTY_CART: IWcwCart = {
  items: [],
  referencesCount: 0,
  articlesCount: 0,
  totalExclTaxProduct: 0,
  totalExclTaxEcoTax: 0,
  totalExclTaxDelivery: 0,
  totalExclTax: 0,
  taxesAmount: 0,
  inclTaxTotal: 0,
};

type Options = {
  get?: OverridableQuery<
    UseQueryOptions<ISuccessApiResponse<IWcwCart>, IErrorApiResponse>
  >;
  add?: UseMutationOptions<
    ISuccessApiResponse<IWcwCart>,
    IErrorApiResponse<unknown, PurchaseErrorCodeKeys, Record<string, unknown>>,
    IPayloadAddWcwCartItems
  >;
  remove?: UseMutationOptions<
    ISuccessApiResponse<IWcwCart>,
    IErrorApiResponse<unknown, PurchaseErrorCodeKeys, Record<string, unknown>>,
    string
  >;
  patch?: UseMutationOptions<
    ISuccessApiResponse<IWcwCart>,
    IErrorApiResponse<unknown, PurchaseErrorCodeKeys, Record<string, unknown>>,
    { itemId: string; quantity: number }
  >;
  reset?: UseMutationOptions<
    ISuccessApiResponse<IWcwCart>,
    IErrorApiResponse<unknown, PurchaseErrorCodeKeys, Record<string, unknown>>
  >;
  submit?: UseMutationOptions<
    ISuccessApiResponse<string[]>,
    IWcwCartValidationErrorResponse,
    { comment?: string }
  >;
  refresh?: OverridableQuery<
    UseQueryOptions<
      ISuccessApiResponse<IWcwCart>,
      IWcwCartValidationErrorResponse
    >
  >;
};

export const useManageWcwCart = <
  TRequestedKeys extends keyof Options,
  TRequestedOptions extends Options = Pick<Options, TRequestedKeys>,
>(
  options: TRequestedOptions & {
    context: { customerId: string };
  } = {} as TRequestedOptions & { context: { customerId: string } },
) => {
  const queryClient = useQueryClient();
  const { callCoreApi } = useApi();

  const idCustomer = options.context?.customerId;

  const getQuery = useQuery({
    queryKey: PurchaseQueryKeys.wcw.base(idCustomer),
    queryFn: () =>
      idCustomer
        ? callCoreApi<ISuccessApiResponse<IWcwCart>>(
            `/offers/api/customer/${idCustomer}/wheel-cart`,
          )
        : emptyQueryError(),
    gcTime: 0,
    refetchOnWindowFocus: false,
    placeholderData: { data: EMPTY_CART, meta: undefined, status: 'SUCCESS' },
    ...options.get,
    enabled: Boolean(idCustomer) && options.get?.enabled,
  });

  const addMutation = useMutation({
    mutationFn: (variables) =>
      idCustomer
        ? callCoreApi<ISuccessApiResponse<IWcwCart>>(
            `/offers/api/customer/${idCustomer}/wheel-cart`,
            { method: 'POST', body: JSON.stringify(variables) },
          )
        : emptyQueryError(),
    keysToInvalidateOnSuccess: [PurchaseQueryKeys.wcw.base(idCustomer)],
    ...options.add,
  });

  const removeMutation = useMutation({
    mutationFn: (itemId) =>
      idCustomer
        ? callCoreApi<ISuccessApiResponse<IWcwCart>>(
            `/offers/api/customer/${idCustomer}/wheel-cart/items/${itemId}`,
            { method: 'DELETE' },
          )
        : emptyQueryError(),
    keysToInvalidateOnSuccess: [PurchaseQueryKeys.wcw.base(idCustomer)],
    ...options.remove,
  });

  const resetMutation = useMutation({
    mutationFn: () =>
      idCustomer
        ? callCoreApi<ISuccessApiResponse<IWcwCart>>(
            `/offers/api/customer/${idCustomer}/wheel-cart`,
            { method: 'DELETE' },
          )
        : emptyQueryError(),
    keysToInvalidateOnSuccess: [PurchaseQueryKeys.wcw.base(idCustomer)],
    ...options.reset,
  });

  const submitMutation = useMutation({
    mutationFn: (variables) =>
      idCustomer
        ? callCoreApi<ISuccessApiResponse<string[]>>(
            `/offers/api/customer/${idCustomer}/wheel-cart/submit`,
            { method: 'POST', body: JSON.stringify(variables) },
          )
        : emptyQueryError(),
    onSuccess(...params) {
      queryClient.setQueryData(PurchaseQueryKeys.wcw.base(idCustomer), {
        status: 'SUCCESS',
        data: EMPTY_CART,
        meta: undefined,
      } satisfies ISuccessApiResponse<IWcwCart>);
      if (options.submit?.onSuccess) {
        options.submit.onSuccess(...params);
      }
    },
    keysToInvalidateOnSuccess: [PurchaseQueryKeys.wcw.base(idCustomer)],
    ...options.submit,
  });

  const refreshQuery = useQuery({
    queryKey: PurchaseQueryKeys.wcw.refresh(idCustomer),
    queryFn: () =>
      idCustomer
        ? callCoreApi<ISuccessApiResponse<IWcwCart>>(
            `/offers/api/customer/${idCustomer}/wheel-cart/items/refresh`,
          )
        : emptyQueryError(),
    retry: false,
    gcTime: 0,
    staleTime: 0,
    refetchOnMount: true,
    retryOnMount: true,
    ...options.refresh,
    enabled: Boolean(options.refresh?.enabled) && getQuery.isFetched,
  });

  return restrictApi(options, {
    get: getQuery,
    add: addMutation,
    remove: removeMutation,
    reset: resetMutation,
    submit: submitMutation,
    refresh: refreshQuery,
  });
};
